import { useEffect, useMemo, useState } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import useQueryObject from '@hooks/useQueryObjectSql';
import { useParams } from '@utils/AvRouter';
import { field } from '@utils/filter';
import { DwQueryRequest, SortDir } from '@views/CustomDashboards/types/QueryObject.types';
import { GraphQLError } from 'graphql';
import { gql } from 'graphql-request';
import { GET_PROCESSES, WorkflowStatus, WorkflowStatusResponse } from '../../../context/AvSnackBarProvider';
import { FileParser } from '../types';

const GET_LAST_WF_ID = gql`
  query getDsiLastFullRunWorkflow($dsiInfo: DsiInfo) {
    getDsiLastFullRunWorkflow(dsiInfo: $dsiInfo) {
      workflowId
    }
  }
`;

const INGEST_FILE_FOR_ANALYTICS = gql`
  mutation ingestFileForAnalytics($details: DataSourcePreviewDetails!) {
    ingestFileForAnalytics(details: $details) {
      workflowRun {
        id
        runId
      }
    }
  }
`;

export const useFileAnalyticsWorkflow = (fileParser?: FileParser) => {
  const { id: dsiId, type: dataSourceType, previewId: fileId } = useParams();
  const [isQueryEnabled, setIsQueryEnabled] = useState(false);
  const [workflowRun, setWorkflowRun] = useState<{ id: string; runId: string } | null>(() => {
    const item = localStorage.getItem(`file-analytics-workflow-${fileId}`);
    return item ? JSON.parse(item) : null;
  });

  const [isStaleWorkflow, setIsStaleWorkflow] = useState(false);
  const [shouldUseRawlogsFallback, setShouldUseRawlogsFallback] = useState(false);

  const { data: lastWorkflow, isLoading: isLoadingLastWorkflowId } = useGqlQuery<{ workflowId: string }>({
    queryKey: [QueryKey.DsiDataWFId, dsiId],
    query: GET_LAST_WF_ID,
    variables: { dsiInfo: { dsiId } },
    onError: (e: GraphQLError) => {
      if (!e.message?.startsWith('Could not find latest full run workflow for dsi')) {
        throw e;
      } else {
        setShouldUseRawlogsFallback(true);
      }
    },
    enabled: !!dsiId,
  });

  // Fallback: when getDsiLastFullRunWorkflow fails (no full run within Temporal retention
  // and the warehouse fallback also returned nothing), query rawlogs directly for the most
  // recent workflow id associated with this DSI — covers incremental-only runs and any
  // ingestion that's still in rawlogs even if Temporal/PG no longer has it.
  const rawlogsWorkflowIdQuery = useMemo<DwQueryRequest>(
    () => ({
      select: {
        dims: [{ name: 'rawlogs.ingest_time' }, { name: "AVALOR_CONTEXT(rawlogs, 'workflowId')", alias: 'workflowId' }],
        metrics: [],
      },
      filter: field('rawlogs.dsi_id').str.equals(dsiId ?? ''),
      projectionId: { name: 'rawlogs', builtIn: true },
      sorting: [{ name: 'rawlogs.ingest_time', dir: SortDir.DESC }],
      top: { size: 1, groupOthers: false },
    }),
    [dsiId]
  );
  const { data: rawlogsFallbackData, isLoading: isLoadingRawlogsFallback } = useQueryObject({
    queryObject: rawlogsWorkflowIdQuery,
    options: { enabled: shouldUseRawlogsFallback && !!dsiId },
  });
  const rawlogsWorkflowId = rawlogsFallbackData?.[0]?.workflowId as string | undefined;

  useEffect(() => {
    if (shouldUseRawlogsFallback && !isLoadingRawlogsFallback && !rawlogsWorkflowId) {
      setIsStaleWorkflow(true);
    }
  }, [shouldUseRawlogsFallback, isLoadingRawlogsFallback, rawlogsWorkflowId]);

  const { data, isLoading: isPolling } = useGqlQuery<WorkflowStatusResponse>({
    queryKey: [QueryKey.MappingAnalyticsWorkflow, workflowRun?.runId],
    query: GET_PROCESSES,
    refetchInterval: 3000,
    enabled: isQueryEnabled,
    variables: {
      runId: workflowRun?.runId,
      wfId: workflowRun?.id,
    },
    onSuccess: data => {
      if (data.status !== WorkflowStatus.RUNNING) {
        setIsQueryEnabled(false);
      }
    },
    onError: () => {
      setIsQueryEnabled(false);
    },
  });

  const { isLoading } = useGqlQuery<{ workflowRun: { id: string; runId: string } }>({
    queryKey: [QueryKey.IngestFileForAnalytics, fileId],
    query: INGEST_FILE_FOR_ANALYTICS,
    variables: {
      details: {
        fileId,
        dataSourceType: dataSourceType?.toUpperCase(),
        reader: fileParser?.reader,
        fileReaderConfig: fileParser?.fileReaderConfig,
      },
    },
    enabled: !!fileId && !workflowRun && !dsiId && !!dataSourceType,
    onSuccess: ({ workflowRun }) => {
      localStorage.setItem(`file-analytics-workflow-${fileId}`, JSON.stringify(workflowRun));
      setWorkflowRun(workflowRun);
      setIsQueryEnabled(true);
    },
  });

  return {
    workflowId: dsiId ? (lastWorkflow?.workflowId ?? rawlogsWorkflowId) : workflowRun?.id,
    isLoading:
      isLoadingLastWorkflowId ||
      (shouldUseRawlogsFallback && isLoadingRawlogsFallback) ||
      isLoading ||
      isPolling ||
      data?.status === WorkflowStatus.RUNNING,
    isStaleWorkflow,
  };
};
